import memoize from 'memoizerific';

import { DamagesTracker, DamgesStatus } from '@ge/models/constants';
import { range } from '@ge/util/array-utils';
import { sorter } from '@ge/util/metric-sorter';

import { fetchDamageDetailsById, fetchDamagesResult } from './damages';
import { getDamageReportDirect } from './downloadFiles';

const PAGE_INDEX = 1;
const PAGE_SIZE = 5000;
const MAX_TOTAL_RECORDS = 30000;
const DAMAGE_COUNTS = 'damage_counts';
const DAMAGES = 'damages';

export const resetDamages = (setDamagesResultData) => {
  setDamagesResultData({ damages: [], damagesCount: {}, isMaxResults: false });
};

export const downloadDamageReport = async (payload) => {
  const options = {
    responseType: 'arraybuffer',
  };
  const data = {
    status: payload.status,
    damage_ids: payload.selectedDamageIds,
    base_url: window.location.origin,
    turbine_damage_mapping: payload.turbineDamageMapping,
  };
  const results = await getDamageReportDirect(data, options);
  return new Promise((resolve) => {
    resolve(results);
  });
};

const updateDamages = (payload) => {
  if (payload !== undefined) {
    const { result, sitesData, setDamagesResultData, isMaxRecord, isDelete, damageCount } = payload;
    let allDamages = [];
    result?.damages?.forEach((damage) => {
      const assetSiteId = damage['Asset Site Id'];
      let updatedDamage = {
        ...damage,
        Site: sitesData[assetSiteId] ? sitesData[assetSiteId].name : '',
        FollowUp: damage['Follow-up'] ? damage['Follow-up'] : '',
        ExternalCaseID: damage['External Case ID'] ? damage['External Case ID'] : '',
        CaseID: damage['Case ID'] ? damage['Case ID'] : '',
        // Convert time from seconds to milliseconds
        'Date Identified': (damage['Date Identified'] || 0) * 1000,
        'Inspection Date': (damage['Inspection Date'] || 0) * 1000,
        'Required Date': (damage['Required Date'] || 0) * 1000,
        'Indication date': (damage['Indication date'] || 0) * 1000,
        cat3_logged_date: (damage['CAT3 Disposition Date'] || 0) * 1000,
        cat3_date: (damage['RUL Start Date'] || 0) * 1000,
        cat3a_logged_date: (damage['CAT3a Disposition Date'] || 0) * 1000,
        cat4_logged_date: (damage['CAT4 Disposition Date'] || 0) * 1000,
        cat5_logged_date: (damage['CAT5 Disposition Date'] || 0) * 1000,
        P50: (damage['P50'] || 0) * 1000,
        P90: (damage['P90'] || 0) * 1000,
      };
      allDamages.push(updatedDamage);
    });
    if (isMaxRecord) {
      setDamagesResultData((prevState) => ({ ...prevState, isMaxResults: true }));
    } else {
      setDamagesResultData((prevState) => ({
        ...prevState,
        isMaxResults: false,
        damages: allDamages,
        damagesCount: result?.damage_counts,
        ...(isDelete && { damageDelCount: damageCount }),
      }));
    }
  }
};

const buildBatchForFetchAllDamages = ({
  batchSize,
  filters,
  endDate,
  startDate,
  pageIndex,
  damage,
  status,
  category,
}) =>
  range(batchSize, pageIndex).map((pageIdx) => {
    return fetchDamagesResult({
      filters,
      endDate,
      startDate,
      pageIdx: pageIdx,
      pageSize: PAGE_SIZE,
      damage,
      status,
      category,
    });
  });

const loadAllDamages = async ({
  filters,
  endDate,
  startDate,
  sitesData,
  setDamagesResultData,
  damage,
  status,
  category,
  damagesCounts,
  damageTracker,
  damageCount,
  isDelete,
}) => {
  let result = [];
  let isMaxRecord = false;
  let damagesCount = 0;
  const loadFeature = async () => {
    try {
      if (damage === 'All' && status === DamgesStatus.STATUS) {
        damagesCount = damagesCounts?.All?.count ? damagesCounts?.All?.count : 0;
        if (damagesCount > MAX_TOTAL_RECORDS) {
          isMaxRecord = true;
          return;
        } else if (damagesCount === 0) {
          isMaxRecord = false;
          return;
        }
      } else if (damage === 'All' && status === DamgesStatus.CLOSED_STATUS) {
        damagesCount = damagesCounts?.AllClosed?.count ? damagesCounts?.AllClosed?.count : 0;
        if (damagesCount > MAX_TOTAL_RECORDS) {
          isMaxRecord = true;
          return;
        } else if (damagesCount === 0) {
          isMaxRecord = false;
          return;
        }
      } else if (damage === 'Blade') {
        damagesCount = damagesCounts?.Blades?.count ? damagesCounts?.Blades?.count : 0;
        if (damagesCount > MAX_TOTAL_RECORDS) {
          isMaxRecord = true;
          return;
        } else if (damagesCount === 0) {
          isMaxRecord = false;
          return;
        }
      } else if (
        damage === 'nonBlade' &&
        damageTracker?.id === DamagesTracker.NON_BLADES_CATEGORY &&
        damageTracker?.selected
      ) {
        damagesCount = damagesCounts?.NonBladesCategory?.count
          ? damagesCounts?.NonBladesCategory?.count
          : 0;
        if (damagesCount > MAX_TOTAL_RECORDS) {
          isMaxRecord = true;
          return;
        } else if (damagesCount === 0) {
          isMaxRecord = false;
          return;
        }
      }

      const totalPages = parseInt(damagesCount / PAGE_SIZE) + 1;
      const batchResults = await Promise.allSettled(
        buildBatchForFetchAllDamages({
          batchSize: totalPages,
          filters,
          endDate,
          startDate,
          pageIndex: 1,
          damage,
          status,
          category,
        }),
      );
      const batchResultsArr = batchResults.map(({ value }) => value.result);
      result = batchResultsArr.reduce((acc, item) => {
        if (acc[DAMAGE_COUNTS]) {
          acc[DAMAGE_COUNTS] = {
            anomalies: acc[DAMAGE_COUNTS]?.anomalies + item[DAMAGE_COUNTS]?.anomalies,
            closed: acc[DAMAGE_COUNTS]?.closed + item[DAMAGE_COUNTS]?.closed,
            open: acc[DAMAGE_COUNTS]?.open + item[DAMAGE_COUNTS]?.open,
          };
        } else {
          acc[DAMAGE_COUNTS] = item[DAMAGE_COUNTS];
        }
        if (acc[DAMAGES]) {
          acc[DAMAGES] = [...acc[DAMAGES], ...item[DAMAGES]];
        } else {
          acc[DAMAGES] = item[DAMAGES];
        }
        return acc;
      }, {});
    } catch (err) {
      console.error('Batch fetch error ', err);
    }
  };
  await loadFeature();
  updateDamages({ result, sitesData, setDamagesResultData, isMaxRecord, damageCount, isDelete });
};

export const fetchDamagesResultTable = async (payload) => {
  if (payload !== undefined || payload === null) {
    const {
      globalFilters,
      sitesData,
      endDate,
      startDate,
      damage,
      status,
      category,
      setDamagesResultData,
      isDamageDetails = false,
      damagesCounts,
      damageTracker,
      damageCount = null,
      isDelete = false,
    } = payload;
    const filters = globalFilters;
    !isDamageDetails && resetDamages(setDamagesResultData);
    return loadAllDamages({
      filters,
      endDate,
      startDate,
      pageIdx: PAGE_INDEX,
      pageSize: PAGE_SIZE,
      sitesData,
      damage,
      status,
      category,
      setDamagesResultData,
      damagesCounts,
      damageTracker,
      damageCount,
      isDelete,
    });
  }
};

export const getDamageDetailsById = async (payload) => {
  const options = {};
  const result = await fetchDamageDetailsById(payload, options);
  return new Promise((resolve) => {
    resolve(result);
  });
};

export const getDamages = memoize(2)((damagesResultData, sortMetric, sortDirection) => {
  var data = Object.values(damagesResultData);
  return data?.sort(sorter(sortMetric, sortDirection)) ?? [];
});
